import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Table, Skeleton, Text, rem, Title, Group, Button, Badge } from "@mantine/core";
import useActiveProcess from "../hooks/useActiveProcess";
import { loadMatrixPei } from "../actions/peiActions";
import { IconDownload } from "@tabler/icons-react";
import html2pdf from "html2pdf.js";

export default function MatrizKpiTable() {
  const dispatch = useDispatch();
  const activeProcess = useActiveProcess();
  const { user } = useSelector((s) => s.auth);
  const { data: pei, loading } = useSelector((s) => s.pei.matrixPei);
  const isLoading = loading && (pei?.Objetivos?.length ?? 0) === 0;
  const hasData = (pei?.Objetivos?.length ?? 0) > 0;

  useEffect(() => {
    if (activeProcess?.Id) {
      dispatch(loadMatrixPei(activeProcess.matriz.Id, user));
    }
  }, [activeProcess?.Id]);

  const tableRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const printCss = useMemo(() => `
    .pdf-root { width: 100%; }
    .pdf-keep { break-inside: avoid; page-break-inside: avoid; }
    .pdf-avoid { break-inside: avoid; page-break-inside: avoid; }
    /* si querés forzar saltos manuales: */
    .html2pdf__page-break { height: 0; padding: 0; margin: 0; border: 0; }
  `, []);

  const handleDownloadSinglePage = async () => {
    if (!tableRef.current || !hasData) return;
    setDownloading(true);
    try {
      const el = tableRef.current;
      
      const w = el.scrollWidth;
      const h = el.scrollHeight;
      const options = {
        filename: "matriz-kpi-una-pagina.pdf",
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 2, useCORS: true, windowWidth: w },
        jsPDF: { unit: "px", format: [w + 20, h + 20], orientation: w > h ? "landscape" : "portrait" },
        pagebreak: { mode: ["css", "avoid-all"] }
      };
      await html2pdf().set(options).from(el).save();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <style>{printCss}</style>

      <div ref={tableRef} className="pdf-root">
        <Table style={{ borderCollapse: "separate", borderSpacing: "8px 8px" }}>
          <Table.Thead>
            <Table.Tr bg={"#d8175b"} c={"white"}>
              <Table.Th w="33%" ta="center" style={{ fontSize: rem(13) }}>
                OBJETIVOS DEL PLAN ESTRATEGICO INSTITUCIONAL
              </Table.Th>
              <Table.Th w="33%" ta="center" style={{ fontSize: rem(13) }}>
                CRITERIOS DE CALIDAD DEFINIDOS EN EL SISTEMA DE MONITOREO DEL PLAN ESTRATEGICO
              </Table.Th>
              <Table.Th w="33%" ta="center" style={{ fontSize: rem(13) }}>
                INDICADORES CLAVE DE RENDIMIENTO (KPI) PARA LAS FACULTADES
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          {isLoading ? (
            <Table.Tbody>
              {Array.from({ length: 6 }).map((_, rowIndex) => (
                <Table.Tr key={`skeleton-${rowIndex}`}>
                  {Array.from({ length: 3 }).map((_, colIndex) => (
                    <Table.Td key={colIndex}><Skeleton height={80} /></Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          ) : (
            pei?.Objetivos?.map((objetivo, objetivoIndex) => {
              const odd = objetivoIndex % 2 === 0;

              return (
                <Table.Tbody key={objetivoIndex} className="pdf-keep">
                  {objetivo.criterios.flatMap((criterio, criterioIndex) =>
                    criterio.kpiList.map((kpi, kpiIndex) => (
                      <Table.Tr key={kpi.Id} className="pdf-avoid">
                        {criterioIndex === 0 && kpiIndex === 0 && (
                          <Table.Td
                            rowSpan={objetivo.criterios.reduce((sum, c) => sum + c.kpiList.length, 0)}
                            bg={odd ? "#f9dce6" : "#f5c8d8"}
                            p="xl"
                            className="pdf-avoid"
                          >
                            <Title order={1} c="#d8175b" fw={400}>{objetivo.numObjetivo}.</Title>
                            <Text size="sm" fw={500}>{objetivo.descripcion}</Text>
                          </Table.Td>
                        )}

                        {kpiIndex === 0 && (
                          <Table.Td
                            rowSpan={criterio.kpiList.length}
                            bg={odd ? "#f9dce6" : "#f5c8d8"}
                            p="xl"
                            className="pdf-avoid"
                          >
                            <Title order={3} c="#d8175b" fw={400}>{criterio.numCriterio}.</Title>
                            <Text size="sm" fw={500}>{criterio.descripcion}</Text>
                            <Group mt="xs" gap="xs">
                              {criterio?.categorias?.map((cat, index) => (
                                <Badge key={index} color="blue" size="sm" variant="dot">{cat.nombre}</Badge>
                              ))}
                            </Group>
                          </Table.Td>
                        )}

                        <Table.Td bg={odd ? "#f9dce6" : "#f5c8d8"} p="xl" className="pdf-avoid">
                          <Text size="sm" fw={500}>
                            <Text span inherit c="#d8175b">KPI {kpi.numKpi}.</Text> {kpi.titulo}
                          </Text>
                          <Text size="xs" mt="xs">{kpi.descripcion}</Text>
                          <Group mt="xs" gap="xs">
                            <Badge color="blue" size="xs" variant="dot">{kpi.categoria}</Badge>
                            <Badge color="teal" size="xs" variant="dot">{kpi.tipo}</Badge>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              );
            })
          )}
        </Table>
      </div>

      <Group justify="center" my="md">
        <Button
          size="compact-md"
          onClick={handleDownloadSinglePage}
          loading={downloading}
          disabled={isLoading || !hasData}
          leftSection={<IconDownload size={18}/>}
        >
          Descargar (1 sola página)
        </Button>
      </Group>
    </>
  );
}
